import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import toastUtils from "../../utils/toastUtils";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Box,
  Chip
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";
import QueueSelectSingle from "../../components/QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: "bold",
  },
  toolsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  toolChip: {
    cursor: "pointer",
  },
}));

const AssistantSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
  description: Yup.string().max(500, "Muito longo!"),
  prompt: Yup.string().min(20, "Muito curto!").required("Descreva as instruções para o assistente"),
  openaiApiKey: Yup.string().required("Informe a API Key da OpenAI"),
  model: Yup.string().required("Selecione o modelo"),
  maxTokens: Yup.number().min(1).max(4000).required("Informe o número máximo de tokens"),
  temperature: Yup.number().min(0).max(2).required("Informe a temperatura"),
  queueId: Yup.number().required("Informe a fila"),
  maxConversationMessages: Yup.number().min(1).max(50).required("Informe o número máximo de mensagens"),
  // Agent mode fields
  agentMode: Yup.string().oneOf(["prompt", "agent_direct", "agent_assistant"]).required("Selecione o modo do agente"),
  allowHumanTakeover: Yup.boolean(),
  transferKeywords: Yup.array().of(Yup.string()),
  enableWebSocketSuggestions: Yup.boolean(),
  suggestionDelay: Yup.number().min(1).max(30),
  suggestContextualResponses: Yup.boolean(),
  suggestCustomerInsights: Yup.boolean()
});

const availableTools = [
  { id: "web_search", name: "Busca na Web", description: "Buscar informações na internet" },
  { id: "calculator", name: "Calculadora", description: "Realizar cálculos matemáticos" },
  { id: "file_reader", name: "Leitor de Arquivos", description: "Ler conteúdo de arquivos" },
  { id: "image_analysis", name: "Análise de Imagem", description: "Analisar conteúdo de imagens" },
  { id: "code_interpreter", name: "Interpretador de Código", description: "Executar e analisar código" },
];

const voiceOptions = [
  { value: "alloy", label: "Alloy - Voz neutra e equilibrada" },
  { value: "echo", label: "Echo - Voz masculina clara" },
  { value: "fable", label: "Fable - Voz feminina suave" },
  { value: "onyx", label: "Onyx - Voz masculina profunda" },
  { value: "nova", label: "Nova - Voz feminina jovem" },
  { value: "shimmer", label: "Shimmer - Voz feminina elegante" },
];

const AssistantModal = ({ open, onClose, assistantId, assistantType = "voice" }) => {
  const classes = useStyles();
  const [showApiKey, setShowApiKey] = useState(false);
  const [selectedTools, setSelectedTools] = useState([]);

  const handleToggleApiKey = () => {
    setShowApiKey(!showApiKey);
  };

  const initialState = {
    name: "",
    description: "",
    prompt: "",
    type: assistantType,
    openaiApiKey: "",
    openaiAssistantId: "",
    model: "gpt-4",
    maxTokens: 1000,
    temperature: 0.7,
    queueId: null,
    // Voice-specific settings
    voiceEnabled: assistantType === "voice",
    ttsVoice: "nova",
    ttsModel: "tts-1",
    whisperModel: "whisper-1",
    maxConversationMessages: 20,
    autoTranscribeVoice: assistantType === "voice",
    autoGenerateVoice: assistantType === "voice",
    // Agent mode configuration
    agentMode: "prompt",
    allowHumanTakeover: true,
    transferKeywords: [],
    enableWebSocketSuggestions: true,
    suggestionDelay: 5,
    suggestContextualResponses: true,
    suggestCustomerInsights: true,
    isActive: true
  };

  const [assistant, setAssistant] = useState(initialState);

  useEffect(() => {
    const fetchAssistant = async () => {
      if (!assistantId) {
        setAssistant({ ...initialState, type: assistantType });
        return;
      }
      try {
        const { data } = await api.get(`/assistants/${assistantId}`);
        setAssistant(prevState => ({ ...prevState, ...data }));
        setSelectedTools(data.tools || []);
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      fetchAssistant();
    }
  }, [assistantId, open, assistantType]);

  const handleClose = () => {
    setAssistant(initialState);
    setSelectedTools([]);
    onClose();
  };

  const handleToolToggle = (toolId) => {
    setSelectedTools(prev => 
      prev.includes(toolId) 
        ? prev.filter(id => id !== toolId)
        : [...prev, toolId]
    );
  };

  const handleSaveAssistant = async (values) => {
    const assistantData = { 
      ...values, 
      type: assistantType,
      tools: selectedTools
    };
    
    if (!values.queueId) {
      toastError("Informe a fila");
      return;
    }
    
    try {
      if (assistantId) {
        await api.put(`/assistants/${assistantId}`, assistantData);
        toastUtils.success(i18n.t("talkAi.assistants.messages.updated"), "Assistente Atualizado");
      } else {
        await api.post("/assistants", assistantData);
        toastUtils.success(i18n.t("talkAi.assistants.messages.created"), "Assistente Criado");
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        scroll="paper"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">
          {assistantId
            ? `${i18n.t("talkAi.assistants.form.editTitle")}`
            : `${i18n.t("talkAi.assistants.form.title")} - ${assistantType === "voice" ? i18n.t("talkAi.assistantType.voiceAssistant") : i18n.t("talkAi.assistantType.textAssistant")}`}
        </DialogTitle>
        <Formik
          initialValues={assistant}
          enableReinitialize={true}
          validationSchema={AssistantSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveAssistant(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                {/* Basic Information */}
                <Typography variant="h6" className={classes.sectionTitle}>
                  {i18n.t("talkAi.assistants.form.basicInfo")}
                </Typography>
                
                <Field
                  as={TextField}
                  label={i18n.t("talkAi.assistants.form.name")}
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                />
                
                <Field
                  as={TextField}
                  label={i18n.t("talkAi.assistants.form.description")}
                  name="description"
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  multiline
                  rows={2}
                />

                <QueueSelectSingle />

                {/* AI Configuration */}
                <Divider style={{ margin: "16px 0" }} />
                <Typography variant="h6" className={classes.sectionTitle}>
                  {i18n.t("talkAi.assistants.form.aiConfiguration")}
                </Typography>

                <FormControl fullWidth margin="dense" variant="outlined">
                  <Field
                    as={TextField}
                    label={i18n.t("talkAi.assistants.form.openaiApiKey")}
                    name="openaiApiKey"
                    type={showApiKey ? 'text' : 'password'}
                    error={touched.openaiApiKey && Boolean(errors.openaiApiKey)}
                    helperText={touched.openaiApiKey && errors.openaiApiKey}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleToggleApiKey}>
                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                <Field
                  as={TextField}
                  label={i18n.t("talkAi.assistants.form.openaiAssistantId")}
                  name="openaiAssistantId"
                  error={touched.openaiAssistantId && Boolean(errors.openaiAssistantId)}
                  helperText={touched.openaiAssistantId && errors.openaiAssistantId}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  placeholder="asst_..."
                />

                <div className={classes.multFieldLine}>
                  <FormControl fullWidth margin="dense" variant="outlined">
                    <InputLabel>{i18n.t("talkAi.assistants.form.model")}</InputLabel>
                    <Field
                      as={Select}
                      name="model"
                      label={i18n.t("talkAi.assistants.form.model")}
                    >
                      <MenuItem value="gpt-4">{i18n.t("talkAi.assistants.models.gpt-4")}</MenuItem>
                      <MenuItem value="gpt-4-turbo-preview">{i18n.t("talkAi.assistants.models.gpt-4-turbo-preview")}</MenuItem>
                      <MenuItem value="gpt-3.5-turbo">{i18n.t("talkAi.assistants.models.gpt-3.5-turbo")}</MenuItem>
                    </Field>
                  </FormControl>

                  <Field
                    as={TextField}
                    label={i18n.t("talkAi.assistants.form.maxTokens")}
                    name="maxTokens"
                    type="number"
                    error={touched.maxTokens && Boolean(errors.maxTokens)}
                    helperText={touched.maxTokens && errors.maxTokens}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />

                  <Field
                    as={TextField}
                    label={i18n.t("talkAi.assistants.form.temperature")}
                    name="temperature"
                    type="number"
                    inputProps={{ step: 0.1, min: 0, max: 2 }}
                    error={touched.temperature && Boolean(errors.temperature)}
                    helperText={touched.temperature && errors.temperature}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>

                <Field
                  as={TextField}
                  label={i18n.t("talkAi.assistants.form.prompt")}
                  name="prompt"
                  error={touched.prompt && Boolean(errors.prompt)}
                  helperText={touched.prompt && errors.prompt}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                  rows={8}
                  multiline={true}
                />

                {/* Tools Configuration */}
                <Divider style={{ margin: "16px 0" }} />
                <Typography variant="h6" className={classes.sectionTitle}>
                  {i18n.t("talkAi.assistants.form.tools")}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {i18n.t("talkAi.assistants.form.toolsDescription")}
                </Typography>
                <div className={classes.toolsContainer}>
                  {availableTools.map((tool) => (
                    <Chip
                      key={tool.id}
                      label={tool.name}
                      clickable
                      className={classes.toolChip}
                      color={selectedTools.includes(tool.id) ? "primary" : "default"}
                      onClick={() => handleToolToggle(tool.id)}
                    />
                  ))}
                </div>

                {/* Voice Settings (only for voice assistants) */}
                {assistantType === "voice" && (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <Typography variant="h6" className={classes.sectionTitle}>
                      {i18n.t("talkAi.assistants.form.voiceSettings")}
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.voiceEnabled}
                          onChange={(e) => setFieldValue("voiceEnabled", e.target.checked)}
                        />
                      }
                      label={i18n.t("talkAi.assistants.form.voiceEnabled")}
                    />

                    {values.voiceEnabled && (
                      <>
                        <div className={classes.multFieldLine}>
                          <FormControl fullWidth margin="dense" variant="outlined">
                            <InputLabel>{i18n.t("talkAi.assistants.form.ttsVoice")}</InputLabel>
                            <Field
                              as={Select}
                              name="ttsVoice"
                              label={i18n.t("talkAi.assistants.form.ttsVoice")}
                            >
                              {voiceOptions.map((voice) => (
                                <MenuItem key={voice.value} value={voice.value}>
                                  {voice.label}
                                </MenuItem>
                              ))}
                            </Field>
                          </FormControl>

                          <FormControl fullWidth margin="dense" variant="outlined">
                            <InputLabel>{i18n.t("talkAi.assistants.form.ttsModel")}</InputLabel>
                            <Field
                              as={Select}
                              name="ttsModel"
                              label={i18n.t("talkAi.assistants.form.ttsModel")}
                            >
                              <MenuItem value="tts-1">{i18n.t("talkAi.assistants.ttsModels.tts-1")}</MenuItem>
                              <MenuItem value="tts-1-hd">{i18n.t("talkAi.assistants.ttsModels.tts-1-hd")}</MenuItem>
                            </Field>
                          </FormControl>
                        </div>

                        <div className={classes.multFieldLine}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.autoTranscribeVoice}
                                onChange={(e) => setFieldValue("autoTranscribeVoice", e.target.checked)}
                              />
                            }
                            label={i18n.t("talkAi.assistants.form.autoTranscribeVoice")}
                          />

                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.autoGenerateVoice}
                                onChange={(e) => setFieldValue("autoGenerateVoice", e.target.checked)}
                              />
                            }
                            label={i18n.t("talkAi.assistants.form.autoGenerateVoice")}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Advanced Settings */}
                <Divider style={{ margin: "16px 0" }} />
                <Typography variant="h6" className={classes.sectionTitle}>
                  {i18n.t("talkAi.assistants.form.advancedSettings")}
                </Typography>

                <Field
                  as={TextField}
                  label={i18n.t("talkAi.assistants.form.maxConversationMessages")}
                  name="maxConversationMessages"
                  type="number"
                  error={touched.maxConversationMessages && Boolean(errors.maxConversationMessages)}
                  helperText={touched.maxConversationMessages && errors.maxConversationMessages}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={values.isActive}
                      onChange={(e) => setFieldValue("isActive", e.target.checked)}
                    />
                  }
                  label={i18n.t("talkAi.assistants.form.isActive")}
                />

                {/* Agent Mode Configuration */}
                <Divider style={{ margin: "16px 0" }} />
                <Typography variant="h6" className={classes.sectionTitle}>
                  {i18n.t("talkAi.assistants.form.agentMode")}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {i18n.t("talkAi.assistants.form.agentModeDescription")}
                </Typography>

                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel>{i18n.t("talkAi.assistants.form.agentModeType")}</InputLabel>
                  <Field
                    as={Select}
                    name="agentMode"
                    label={i18n.t("talkAi.assistants.form.agentModeType")}
                  >
                    <MenuItem value="prompt">{i18n.t("talkAi.assistants.agentModes.prompt")}</MenuItem>
                    <MenuItem value="agent_direct">{i18n.t("talkAi.assistants.agentModes.agentDirect")}</MenuItem>
                    <MenuItem value="agent_assistant">{i18n.t("talkAi.assistants.agentModes.agentAssistant")}</MenuItem>
                  </Field>
                </FormControl>

                {/* Agent Mode Settings - only for agent modes */}
                {(values.agentMode === "agent_direct" || values.agentMode === "agent_assistant") && (
                  <>
                    <div className={classes.multFieldLine}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.allowHumanTakeover}
                            onChange={(e) => setFieldValue("allowHumanTakeover", e.target.checked)}
                          />
                        }
                        label={i18n.t("talkAi.assistants.form.allowHumanTakeover")}
                      />
                    </div>

                    <TextField
                      label={i18n.t("talkAi.assistants.form.transferKeywords")}
                      name="transferKeywords"
                      value={Array.isArray(values.transferKeywords) ? values.transferKeywords.join(", ") : ""}
                      onChange={(e) => {
                        console.log("🔧 Transfer keywords input:", e.target.value);
                        const keywords = e.target.value.split(",").map(k => k.trim()).filter(k => k);
                        console.log("🔧 Processed keywords:", keywords);
                        setFieldValue("transferKeywords", keywords);
                      }}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="suporte, ajuda, humano, atendente"
                      helperText={i18n.t("talkAi.assistants.form.transferKeywordsHelp") + " (separar por vírgulas)"}
                    />

                    {/* WebSocket Suggestions - only for agent_assistant mode */}
                    {values.agentMode === "agent_assistant" && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.enableWebSocketSuggestions}
                              onChange={(e) => setFieldValue("enableWebSocketSuggestions", e.target.checked)}
                            />
                          }
                          label={i18n.t("talkAi.assistants.form.enableWebSocketSuggestions")}
                        />

                        {values.enableWebSocketSuggestions && (
                          <>
                            <Field
                              as={TextField}
                              label={i18n.t("talkAi.assistants.form.suggestionDelay")}
                              name="suggestionDelay"
                              type="number"
                              inputProps={{ min: 1, max: 30 }}
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              helperText={i18n.t("talkAi.assistants.form.suggestionDelayHelp")}
                            />

                            <div className={classes.multFieldLine}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.suggestContextualResponses}
                                    onChange={(e) => setFieldValue("suggestContextualResponses", e.target.checked)}
                                  />
                                }
                                label={i18n.t("talkAi.assistants.form.suggestContextualResponses")}
                              />

                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.suggestCustomerInsights}
                                    onChange={(e) => setFieldValue("suggestCustomerInsights", e.target.checked)}
                                  />
                                }
                                label={i18n.t("talkAi.assistants.form.suggestCustomerInsights")}
                              />
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </DialogContent>
              
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("talkAi.assistants.form.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {assistantId
                    ? `${i18n.t("talkAi.assistants.form.save")}`
                    : `${i18n.t("talkAi.assistants.form.save")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default AssistantModal;